import { useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { getBillingInfo, updateBillingInfo } from "@/server/billing";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { logError } from "@/lib/structured-log";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
  "/workspace/$workspaceSlug/settings/workspace/billing/billing-info",
)({
  loader: async ({ context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "billing-info",
      parentMatchPromise,
    );
    const billingInfo = await getBillingInfo({ data: workspaceId });
    return { billingInfo, workspaceId };
  },
  component: BillingInfoPage,
});

function BillingInfoPage() {
  const { billingInfo, workspaceId } = Route.useLoaderData();
  const { t } = useTranslation("billing");
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  // Simple state for form, in a real app use react-hook-form
  const [formData, setFormData] = useState({
    company_name: billingInfo?.company_name || "",
    tax_id: billingInfo?.tax_id || "",
    address_line1: billingInfo?.address_line1 || "",
    city: billingInfo?.city || "",
    country: billingInfo?.country || "",
    billing_email: billingInfo?.billing_email || "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateBillingInfo({
        data: {
          workspaceId,
          data: formData,
        },
      });
      toast.success(t("billing_info.saved", "Billing information saved"));
      router.invalidate(); // Refresh data
    } catch (error) {
      toast.error(t("billing_info.save_failed", "Failed to update billing information"));
      logError("billing.update_billing_info_failed", {
        workspaceId,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-2xl">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">
          {t("billing_info.title", "Billing information")}
        </h2>
        <p className="text-muted-foreground">
          {t("billing_info.page_description", "Manage your company details for invoices.")}
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>{t("billing_info.details_title", "Company details")}</CardTitle>
            <CardDescription>
              {t("billing_info.details_description", "These details will appear on your invoices.")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">
                {t("billing_info.company_name", "Business/Institution name")}
              </Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder={t("billing_info.placeholders.company_name", "Acme Inc.")}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_id">{t("billing_info.tax_id", "VAT/GST ID")}</Label>
              <Input
                id="tax_id"
                name="tax_id"
                value={formData.tax_id}
                onChange={handleChange}
                placeholder={t("billing_info.placeholders.tax_id", "Tax ID")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing_email">{t("billing_info.email", "Billing email")}</Label>
              <Input
                id="billing_email"
                name="billing_email"
                type="email"
                value={formData.billing_email}
                onChange={handleChange}
                placeholder={t("billing_info.placeholders.billing_email", "billing@example.com")}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line1">
                {t("billing_info.address1", "Address (Street, P.O. box)")}
              </Label>
              <Input
                id="address_line1"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                placeholder={t("billing_info.placeholders.address_line1", "123 Main St")}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">{t("billing_info.city", "City")}</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder={t("billing_info.placeholders.city", "San Francisco")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">{t("billing_info.country", "Country/Region")}</Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder={t("billing_info.placeholders.country", "USA")}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("actions.save", "Save billing information")}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
